/* eslint-disable react/react-in-jsx-scope */
import CardTypeProject from "@/components/card-v2/CardTypeProject";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import i18n from "@/i18n";
import { Loader2 } from "lucide-react";
import { useMemo, useState } from "react";

type ProjectTypeSelectionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  typesProject: any[];
  loading?: boolean;
  loadingTypes?: boolean;
  onConfirm: (payload: { typeId: number; typeLabel: string }) => void | Promise<void>;
  title?: string;
  description?: string;
};

const ProjectTypeSelectionDialog = ({
  open,
  onOpenChange,
  typesProject,
  loading = false,
  loadingTypes = false,
  onConfirm,
  title = "Typologie des projets",
  description = "Veuillez selectionner un type de projet pour creer le projet.",
}: ProjectTypeSelectionDialogProps) => {
  const [typeGroup, setTypeGroup] = useState<1 | 2>(1);
  const [selectedTypeName, setSelectedTypeName] = useState<string | null>(null);
  const [activeParentFilter, setActiveParentFilter] = useState<string>("all");

  const filteredTypes = useMemo(() => {
    const all = Array.isArray(typesProject) ? typesProject : [];
    const byGroup = all.filter((type) => Number(type?.type) === Number(typeGroup));
    if (activeParentFilter === "all") return byGroup;
    return byGroup.filter((item: any) => item?.parent?.name === activeParentFilter);
  }, [activeParentFilter, typeGroup, typesProject]);

  const parentFilters = useMemo(() => {
    const all = Array.isArray(typesProject) ? typesProject : [];
    const byGroup = all.filter((type) => Number(type?.type) === Number(typeGroup));
    const unique: any[] = [];
    byGroup.forEach((item: any) => {
      const parent = item?.parent;
      if (!parent?.name) return;
      if (!unique.some((u) => u.name === parent.name)) {
        unique.push(parent);
      }
    });
    return unique;
  }, [typeGroup, typesProject]);

  const toLocalizedName = (typeItem: any): string => {
    const langCode = i18n.language?.split("-")[0];
    const localized = Array.isArray(typeItem?.projectTypeLangs)
      ? typeItem.projectTypeLangs.find(
          (entry: any) =>
            entry?.lang?.code === i18n.language || entry?.lang?.code === langCode,
        )?.name
      : undefined;
    return (localized || typeItem?.name || "").trim();
  };

  const handleConfirm = async () => {
    if (!selectedTypeName) return;
    const selectedType = (Array.isArray(typesProject) ? typesProject : []).find(
      (item: any) => String(item?.name) === String(selectedTypeName),
    );
    const typeLabel = toLocalizedName(selectedType);
    await onConfirm({ typeId: Number(selectedType?.id ?? 0), typeLabel });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && loading) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="sm:h-[95vh] sm:max-w-[80vw] sm:overflow-hidden">
        <DialogHeader>
          <DialogTitle asChild>
            <h2 className="mb-2 mt-5 flex items-center justify-center gap-4 text-center text-xl font-bold">
              {title}
            </h2>
          </DialogTitle>
          <DialogDescription asChild className="text-[#595959]">
            <p className="mb-0 text-center sm:mx-auto sm:max-w-[60vw]">{description}</p>
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <button
            type="button"
            className={`relative rounded-[10px] px-[15px] py-[10px] text-sm font-[600] ${
              typeGroup === 1 ? "bg-[#f2f7ec] text-verdeoorange" : "bg-[rgba(194,194,194,0.1)] text-[#2D3720]"
            }`}
            onClick={() => setTypeGroup(1)}
            disabled={loading}
          >
            Type environnemental
            {typeGroup === 1 && (
              <div
                style={{ width: "calc(100% - 6px)" }}
                className="absolute bottom-0 left-[3px] h-[3px] rounded-[2px] bg-verdeoorange"
              />
            )}
          </button>
          <button
            type="button"
            className={`relative rounded-[10px] px-[15px] py-[10px] text-sm font-[600] ${
              typeGroup === 2 ? "bg-[#fff5ea] text-verdeoorange" : "bg-[rgba(194,194,194,0.1)] text-[#2D3720]"
            }`}
            onClick={() => setTypeGroup(2)}
            disabled={loading}
          >
            Type social
            {typeGroup === 2 && (
              <div
                style={{ width: "calc(100% - 6px)" }}
                className="absolute bottom-0 left-[3px] h-[3px] rounded-[2px] bg-verdeoorange"
              />
            )}
          </button>
        </div>

        <div
          className={`relative rounded-[20px] ${
            typeGroup === 1 ? "bg-[#f2f7ec]" : "bg-[#fff5ea]"
          } p-[20px]`}
        >
          <div className="mx-auto mb-3 flex w-full flex-wrap justify-start gap-2">
            {loadingTypes ? (
              <>
                <Skeleton className="h-7 w-24 rounded-full" />
                <Skeleton className="h-7 w-28 rounded-full" />
                <Skeleton className="h-7 w-20 rounded-full" />
              </>
            ) : (
              <>
                {parentFilters.map((parent: any) => (
                  <Badge
                    key={String(parent?.id ?? parent?.name)}
                    variant={activeParentFilter === parent.name ? "default" : "outline"}
                    onClick={() => setActiveParentFilter(parent.name)}
                    className={`cursor-pointer ${
                      activeParentFilter === parent.name ? "bg-verdeoorange hover:bg-verdeoorange" : ""
                    }`}
                  >
                    {toLocalizedName(parent) || parent.name}
                  </Badge>
                ))}
                <Badge
                  variant={activeParentFilter === "all" ? "default" : "outline"}
                  onClick={() => setActiveParentFilter("all")}
                  className={`cursor-pointer ${
                    activeParentFilter === "all" ? "bg-verdeoorange hover:bg-verdeoorange" : ""
                  }`}
                >
                  Toutes
                </Badge>
              </>
            )}
          </div>

          <ScrollArea style={{ height: "calc(100vh - 430px)", marginBottom: "6em" }}>
            <div className="flex flex-wrap pt-2 sm:pb-52">
            {loadingTypes ? (
              Array.from({ length: 10 }).map((_, index) => (
                <div key={`type-skeleton-${index}`} className="relative mb-4 basis-full px-4 md:basis-1/5">
                  <div className="overflow-hidden rounded-[24px] border border-[#e4ebdf] bg-white p-4 shadow-sm">
                    <Skeleton className="mb-4 h-28 w-full rounded-[18px]" />
                    <Skeleton className="mb-2 h-5 w-3/4 rounded-md" />
                    <Skeleton className="h-4 w-full rounded-md" />
                    <Skeleton className="mt-2 h-4 w-5/6 rounded-md" />
                  </div>
                </div>
              ))
            ) : (
              filteredTypes.map((item: any) => {
                const selected = String(selectedTypeName ?? "") === String(item?.name ?? "");
                return (
                  <div key={item.id} className="relative mb-4 basis-full px-4 md:basis-1/5">
                    <CardTypeProject
                      icon={item.icon}
                      name={toLocalizedName(item)}
                      description={item?.description}
                      isActive={selected}
                      type={item.type}
                    />
                    <Checkbox
                      className="absolute left-4 top-0 z-30 h-full opacity-0"
                      style={{ width: "calc(100% - 30px)" }}
                      checked={selected}
                      onCheckedChange={(checked) => {
                        if (loading || loadingTypes) return;
                        setSelectedTypeName(checked ? String(item.name) : null);
                      }}
                    />
                  </div>
                );
              })
            )}
            </div>
          </ScrollArea>

          <DialogFooter className="absolute bottom-0 left-0 z-50 flex w-full justify-center bg-gradient-to-t from-background to-background/0 py-8">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Annuler
            </Button>
            <Button type="button" onClick={handleConfirm} disabled={loading || loadingTypes || !selectedTypeName}>
              {loading ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  Creation...
                </span>
              ) : loadingTypes ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  Chargement...
                </span>
              ) : (
                "Creer le projet"
              )}
            </Button>
          </DialogFooter>
          </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectTypeSelectionDialog;
